/**
 * ThreadListSheet — mobile bottom-sheet thread list (mobile UX round,
 * 2026-08-14).
 *
 * On a phone the horizontal chip strip shows ~2 thread names and switching
 * means blind horizontal scrolling; this sheet is the vertical answer. It is
 * deliberately a THIRD RENDERER of the same data, not a new data source:
 * rows are the fullscreen sidebar's exact anatomy (ThreadSidebarRow family,
 * dark-rail palette), ordering is the shared `orderThreads`, the archive ×
 * routes through the same `shouldRouteCloseToEmptyDelete` rule, and every
 * callback is the SAME handler object the chip strip receives at the host
 * render site — the three thread surfaces can never disagree.
 *
 * Additive by construction: ThreadSwitcherBar's pinned DOM is untouched; the
 * trigger renders host-side beside the strip (mobile only) and the sheet
 * body-portals over the fullscreen takeover.
 *
 * Portal contract (styles.ts:380s doctrine + portalClickGuard): the scrim
 * and sheet BOTH sit at BODY_PORTAL_Z_INDEX and BOTH carry
 * data-prest0n-portal="true" — without the marker, useGlobalChat's
 * outside-click handler would close the whole chat on any sheet tap (the
 * exact defect the 2026-08-14 review caught on the header overflow menu).
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ThreadSheetScrim,
  ThreadSheetContainer,
  ThreadSheetHandle,
  ThreadSheetHeader,
  ThreadSheetTitle,
  ThreadSheetList,
  ThreadSidebarRow,
  ThreadSidebarRowMain,
  ThreadSidebarRowTitle,
  ThreadSidebarRowMeta,
  ThreadChipUnreadBadge,
  ThreadChipPinButton,
  ThreadChipCloseButton,
  ThreadNewButton,
} from './styles';
import type { ChatThread } from './types';
import { ARCHIVE_ALWAYS_AVAILABLE, orderThreads } from './useChatThreads';
import {
  ThreadTurnIndicator,
  relativeActivityLabel,
  shouldRouteCloseToEmptyDelete,
} from './ThreadSwitcherBar';
import { RoutineGlyph, routineNextRunLabel, routineThreadDisplayName } from './RoutineGlyph';
import { ThreadTypeGlyph } from './ThreadTypeGlyph';
import { isGroupParticipantCount } from '../../services/groupThreads/senderIdentity';
import { RoutineRowTitleLine, RoutineNextRunMeta, RoutinePausedChip } from './Routines.styles';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

export interface ThreadListSheetProps {
  open: boolean;
  onClose: () => void;
  threads: ChatThread[];
  activeThreadId: string | null;
  processingByThread?: Record<string, boolean>;
  turnStartedAtByThread?: Record<string, number>;
  activeThreadMessageCount?: number;
  participantsByThread?: Record<string, number>;
  onSwitch: (threadId: string) => void;
  onNew: () => void;
  onRequestDelete: (threadId: string) => void;
  onDeleteEmptyThread?: (threadId: string) => void;
  onTogglePin?: (threadId: string) => void;
  /**
   * Docked thread-list round (2026-08-24): the same list wears two forms.
   * 'sheet' (default) is the mobile bottom sheet, unchanged. 'panel' is the
   * docked pane's form — an anchored drop-UP menu above the strip's ☰
   * trigger (SidebarAccountMenu family: dark surface, gold hairline, opens
   * upward), with an invisible click-away layer instead of a page dim.
   * One renderer, one data source, two silhouettes.
   */
  variant?: 'sheet' | 'panel';
  /**
   * Panel-form anchor, measured from the trigger at open time (the
   * HeaderOverflowMenu idiom): distances from the viewport's right and
   * bottom edges. Ignored by the sheet form.
   */
  anchor?: { right: number; bottom: number } | null;
}

export const ThreadListSheet: React.FC<ThreadListSheetProps> = ({
  open,
  onClose,
  threads,
  activeThreadId,
  processingByThread,
  turnStartedAtByThread,
  activeThreadMessageCount,
  participantsByThread,
  onSwitch,
  onNew,
  onRequestDelete,
  onDeleteEmptyThread,
  onTogglePin,
  variant = 'sheet',
  anchor,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Reference-counted shared lock — nests cleanly with the fullscreen
  // takeover's own lock (threadScrollingUx contract).
  useBodyScrollLock(open);

  // Panel form only: the anchor was measured from the trigger at open time,
  // so a viewport resize makes it stale — close rather than float loose
  // (standard anchored-menu behavior; the scroll case can't drift because
  // the body scroll lock above holds the page still while open).
  useEffect(() => {
    if (!open || variant !== 'panel') return undefined;
    const onResize = () => onClose();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, variant, onClose]);

  // Escape closes (capture phase, same contract as ThreadHeaderOverflowMenu:
  // stopPropagation so the chat's own Escape handling doesn't also fire).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose]);

  // Focus contract (review 2026-08-14 — the sibling portals' own pattern:
  // ThreadHeaderOverflowMenu focuses its first menuitem on open and returns
  // focus on close): an aria-modal dialog must take focus, or a keyboard
  // user is left tabbing the chrome BEHIND the scrim. First thread row on
  // open (sheet container as fallback); previously-focused element — the
  // trigger — restored on close/unmount.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const first = document.querySelector<HTMLElement>('[data-sheet-thread-id]')
      || sheetRef.current;
    first?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, [open]);

  const handleClose = useCallback((e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const routeToEmptyDelete = shouldRouteCloseToEmptyDelete({
      threadId,
      activeThreadId,
      threads,
      activeThreadMessageCount,
      hasEmptyDeleteHandler: !!onDeleteEmptyThread,
    });
    if (routeToEmptyDelete && onDeleteEmptyThread) {
      // Sheet stays open: the row simply vanishes — no modal is coming.
      onDeleteEmptyThread(threadId);
      return;
    }
    onRequestDelete(threadId);
    // The archive confirm modal takes the screen next — close the sheet so
    // there is exactly ONE layer for Escape/cancel to target. Leaving it
    // open put the modal over a live hidden sheet whose capture-phase
    // Escape listener dismissed the WRONG layer (review 2026-08-14).
    onClose();
  }, [activeThreadId, threads, activeThreadMessageCount, onDeleteEmptyThread, onRequestDelete, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const ordered = orderThreads(threads);

  const renderRow = (thread: ChatThread): React.ReactNode => {
    const isActive = thread.id === activeThreadId;
    const isRoutine = !!thread.routineId;
    // v2 (2026-08-16): group-ness is independent of routine-ness — a routine
    // group thread wears both markers (mirrors the sidebar rule).
    const isGroup = thread.sharedThread === true
      || isGroupParticipantCount(participantsByThread?.[thread.id] ?? 1);
    const routinePaused = thread.routineStatus === 'paused';
    const displayTitle = isRoutine ? routineThreadDisplayName(thread) : thread.title;
    const isTurnRunning = !!(processingByThread && processingByThread[thread.id]);
    const turnSinceMs = isTurnRunning ? (turnStartedAtByThread?.[thread.id] ?? null) : null;
    const showBadge = !isActive && thread.unreadCount > 0;
    // v2: trigger routines have no clock — the cadence line is the trigger
    // summary; multi-routine threads lead with their count.
    const routineCadence = routinePaused
      ? 'Paused'
      : routineNextRunLabel(thread.routineNextRunAt)
        || (thread.routineNextRunAt == null ? (thread.routineScheduleSummary || '') : '');
    const routineMetaText = (thread.routineCount ?? 1) > 1
      ? [`${thread.routineCount} routines`, routineCadence].filter(Boolean).join(' · ')
      : routineCadence;
    return (
      <ThreadSidebarRow
        key={thread.id}
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-label={
          `${isRoutine ? 'routine ' : ''}${displayTitle}${isTurnRunning ? ' (working)' : ''}` +
          (showBadge ? `, ${thread.unreadCount} unread` : '')
        }
        data-sheet-thread-id={thread.id}
        $active={isActive}
        title={thread.topicSummary || displayTitle}
        onClick={() => {
          if (!isActive) onSwitch(thread.id);
          onClose();
        }}
      >
        <ThreadSidebarRowMain>
          {isRoutine ? (
            <>
              <RoutineRowTitleLine>
                <RoutineGlyph active={!routinePaused} size={12} />
                {isGroup && (
                  <ThreadTypeGlyph kind="group" active={isActive} size={12} data-routine-group-glyph="" />
                )}
                <ThreadSidebarRowTitle $active={isActive}>{displayTitle}</ThreadSidebarRowTitle>
                {routinePaused && (
                  <RoutinePausedChip data-routine-paused-chip="">Paused</RoutinePausedChip>
                )}
              </RoutineRowTitleLine>
              {routineMetaText && (
                <RoutineNextRunMeta data-routine-row-meta="">{routineMetaText}</RoutineNextRunMeta>
              )}
            </>
          ) : (
            <>
              <RoutineRowTitleLine>
                <ThreadTypeGlyph kind={isGroup ? 'group' : 'individual'} active={isActive} size={12} />
                <ThreadSidebarRowTitle $active={isActive}>{thread.title}</ThreadSidebarRowTitle>
              </RoutineRowTitleLine>
              <ThreadSidebarRowMeta>
                {relativeActivityLabel(thread.lastActiveAt)}
                {thread.messageCount > 0 ? ` · ${thread.messageCount} messages` : ' · empty'}
              </ThreadSidebarRowMeta>
            </>
          )}
        </ThreadSidebarRowMain>
        {isTurnRunning && <ThreadTurnIndicator sinceMs={turnSinceMs} />}
        {showBadge && (
          <ThreadChipUnreadBadge aria-hidden="true">
            {thread.unreadCount > 99 ? '99+' : String(thread.unreadCount)}
          </ThreadChipUnreadBadge>
        )}
        {onTogglePin && (
          <ThreadChipPinButton
            type="button"
            data-thread-pin-btn=""
            data-pinned={thread.pinned === true ? 'true' : 'false'}
            $pinned={thread.pinned === true}
            tabIndex={-1}
            aria-pressed={thread.pinned === true}
            aria-label={thread.pinned ? `unpin thread ${thread.title}` : `pin thread ${thread.title} to the top`}
            title={thread.pinned ? 'unpin thread' : 'pin thread to top'}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onTogglePin(thread.id);
            }}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M7.2 1.2 L10.8 4.8 L8.6 5.2 L6.4 7.4 L6 10.8 L3.8 8.6 L1.2 11.2 M3.8 8.6 L1.6 6.4 L5 6 L7.2 3.8 Z"
                fill={thread.pinned ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </ThreadChipPinButton>
        )}
        {ARCHIVE_ALWAYS_AVAILABLE && (
          <ThreadChipCloseButton
            type="button"
            data-thread-close-btn=""
            $onDark
            tabIndex={-1}
            aria-label={`archive thread ${thread.title} (Prest0n keeps it in memory for reference; the conversation closes)`}
            title="archive thread"
            onClick={(e) => handleClose(e, thread.id)}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path d="M3 3 L9 9 M9 3 L3 9" strokeLinecap="round" />
            </svg>
          </ThreadChipCloseButton>
        )}
      </ThreadSidebarRow>
    );
  };

  return createPortal(
    <>
      <ThreadSheetScrim
        data-thread-sheet-scrim=""
        data-prest0n-portal="true"
        $transparent={variant === 'panel'}
        onClick={onClose}
      />
      <ThreadSheetContainer
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="threads"
        tabIndex={-1}
        data-thread-sheet=""
        data-thread-sheet-variant={variant}
        data-prest0n-portal="true"
        $variant={variant}
        style={variant === 'panel' && anchor
          ? { right: anchor.right, bottom: anchor.bottom }
          : undefined}
      >
        {variant === 'sheet' && <ThreadSheetHandle aria-hidden="true" />}
        <ThreadSheetHeader>
          <ThreadSheetTitle>
            threads
            {ordered.length > 0 ? ` · ${ordered.length}` : ''}
          </ThreadSheetTitle>
          <ThreadNewButton
            type="button"
            data-thread-sheet-new=""
            aria-label="start a new thread"
            title="new thread"
            onClick={() => {
              onNew();
              onClose();
            }}
          >
            +
          </ThreadNewButton>
        </ThreadSheetHeader>
        <ThreadSheetList role="tablist" aria-orientation="vertical">
          {ordered.map(renderRow)}
        </ThreadSheetList>
      </ThreadSheetContainer>
    </>,
    document.body,
  );
};

export default ThreadListSheet;
